'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { LeagueMatchCard } from './LeagueMatchCard'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

interface CarouselMatch {
  matchNumber: string
  giornata: number
  date: string
  time: string
  home: string
  away: string
  homeLogoSrc?: string | null
  awayLogoSrc?: string | null
  result: string | null
  sets: string | null
  played: boolean
  venue?: string | null
  referees?: string | null
  competition?: string
  championshipSlug: string
  championshipTitle: string
  poolSlug: string
  stagioneId?: string
  badgePath?: string | null
}

interface MatchCarouselProps {
  matches: CarouselMatch[]
  emptyMessage?: string
  /** Stato senza calendari: campionati censiti come badge selettori disattivi
   *  e una box che copre larghezza e altezza di due slide dello slider. */
  placeholder?: {
    championships: Array<{ slug: string; title: string }>
    message: string
  }
}

const TRANSITION = 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)'

function matchUrl(m: CarouselMatch) {
  const base = `/campionati/${m.championshipSlug}/${m.poolSlug}/${m.matchNumber}`
  return m.stagioneId ? `${base}?stagione=${m.stagioneId}` : base
}

function CardPair({ a, b }: { a: CarouselMatch; b: CarouselMatch | null }) {
  return (
    <div className="flex flex-col gap-3">
      <Link
        href={matchUrl(a)}
        className="league-press block min-w-0 rounded-2xl transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--league-brand-vivid)]"
      >
        <LeagueMatchCard match={a} variant="compact" noLink />
      </Link>
      {b && (
        <Link
          href={matchUrl(b)}
          className="league-press block min-w-0 rounded-2xl transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--league-brand-vivid)]"
        >
          <LeagueMatchCard match={b} variant="compact" noLink />
        </Link>
      )}
    </div>
  )
}

function useCarousel(total: number, onCycleComplete?: () => void) {
  const reduceMotion = usePrefersReducedMotion()
  const [activeIdx, setActiveIdx] = useState(0)
  const [sliding, setSliding] = useState(false)
  const [resetToken, setResetToken] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const commitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const totalRef = useRef(total)
  const onCycleRef = useRef(onCycleComplete)
  totalRef.current = total
  onCycleRef.current = onCycleComplete

  function startAutoplay() {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (reduceMotion || totalRef.current <= 2) return
    intervalRef.current = setInterval(() => setSliding(true), 4000)
  }

  useEffect(() => {
    if (commitRef.current) clearTimeout(commitRef.current)
    setSliding(false)
    startAutoplay()
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (commitRef.current) clearTimeout(commitRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, resetToken, reduceMotion])

  useEffect(() => {
    if (!sliding) return
    commitRef.current = setTimeout(() => {
      setActiveIdx((prev) => {
        const next = (prev + 2) % Math.max(totalRef.current, 1)
        // Completed a full cycle — notify parent to advance championship
        if (next === 0 && totalRef.current > 2) {
          setTimeout(() => onCycleRef.current?.(), 0)
        }
        return next
      })
      setSliding(false)
    }, 370)
    return () => { if (commitRef.current) clearTimeout(commitRef.current) }
  }, [sliding])

  function reset() {
    setActiveIdx(0)
    setResetToken((t) => t + 1)
  }

  function goTo(page: number) {
    setActiveIdx(page * 2)
    setResetToken((t) => t + 1)
  }

  return { activeIdx, sliding, goTo, reset, reduceMotion }
}

export function MatchCarousel({ matches, emptyMessage = 'Nessuna partita disponibile.', placeholder }: MatchCarouselProps) {
  // Build unique championship list sorted by CHAMP_ORDER
  // Preserve the order championships appear in the matches array (respects DB display_order)
  const seen = new Set<string>()
  const championships: { slug: string; title: string }[] = []
  for (const m of matches) {
    if (!seen.has(m.championshipSlug)) {
      seen.add(m.championshipSlug)
      championships.push({ slug: m.championshipSlug, title: m.championshipTitle })
    }
  }

  // Use '' as stable initial state (avoids SSR/client mismatch).
  // effectiveChamp falls back to first available championship.
  const [activeChamp, setActiveChamp] = useState('')
  const effectiveChamp = championships.find((c) => c.slug === activeChamp)?.slug ?? championships[0]?.slug ?? ''

  const filtered = matches.filter((m) => m.championshipSlug === effectiveChamp)
  const total = filtered.length

  // resetRef lets advanceChamp call reset() even though reset comes from the hook below
  const resetRef = useRef<() => void>(() => {})

  function advanceChamp() {
    const currentIndex = championships.findIndex((c) => c.slug === effectiveChamp)
    const nextChamp = championships[(currentIndex + 1) % championships.length]
    if (nextChamp && nextChamp.slug !== effectiveChamp) {
      setActiveChamp(nextChamp.slug)
      resetRef.current()
    }
  }

  const { activeIdx, sliding, goTo, reset, reduceMotion } = useCarousel(total, championships.length > 1 ? advanceChamp : undefined)
  resetRef.current = reset

  function selectChamp(slug: string) {
    setActiveChamp(slug)
    reset()
  }

  if (matches.length === 0) {
    if (placeholder) {
      return (
        <div>
          {/* Gli stessi badge selettori di campionato dello slider, disattivati:
              i calendari della stagione non sono ancora pubblicati. */}
          {placeholder.championships.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {placeholder.championships.map((champ) => (
                <button
                  key={champ.slug}
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="min-h-11 cursor-not-allowed rounded-full bg-[color:var(--league-surface-alt)] px-3 py-1 text-[12px] font-medium text-[color:var(--league-muted)] opacity-60"
                >
                  {champ.title}
                </button>
              ))}
            </div>
          )}

          {/* Box a piena larghezza che copre l'ingombro di due slide dello slider
              (2 card compatte ~197px + gap 12px). */}
          <div className="league-card flex min-h-[406px] items-center justify-center rounded-2xl px-6 py-10 text-center">
            <p className="max-w-[46ch] text-[16px] leading-7 text-[color:var(--league-muted)]">
              {placeholder.message}
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="league-card rounded-2xl px-5 py-6 text-center text-[16px] text-[color:var(--league-muted)]">
        {emptyMessage}
      </div>
    )
  }

  const nextIdx = (activeIdx + 2) % Math.max(total, 1)
  const a = filtered[activeIdx % Math.max(total, 1)]
  const b = total > 1 ? filtered[(activeIdx + 1) % total] : null
  const c = filtered[nextIdx % Math.max(total, 1)]
  const d = total > 1 ? filtered[(nextIdx + 1) % total] : null

  const pageCount = Math.ceil(total / 2)
  const activePage = Math.floor(activeIdx / 2) % Math.max(pageCount, 1)

  // Il badge del campionato segue i campionati **presenti nella lista** (quindi
  // i gironi abilitati che hanno partite): con uno solo è un'etichetta statica,
  // con più di uno diventa il selettore dello slider. Nessun flag esterno.
  const showChampionshipNav = championships.length > 0

  return (
    <div>
      {/* Championship badge/tabs */}
      {showChampionshipNav && (
        <div className="mb-4 flex flex-wrap gap-2">
          {championships.length > 1 ? (
            championships.map((champ) => (
              <button
              key={champ.slug}
              onClick={() => selectChamp(champ.slug)}
              className={`league-press min-h-11 rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${
                  champ.slug === effectiveChamp
                    ? 'bg-[color:var(--league-brand-solid)] text-white'
                    : 'bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)] hover:text-[color:var(--league-ink)]'
                }`}
              >
                {champ.title}
              </button>
            ))
          ) : (
            <span className="league-badge min-h-11 bg-[color:var(--league-brand-solid)] text-white">
              {championships[0]?.title}
            </span>
          )}
        </div>
      )}

      {total === 0 ? (
        <div className="league-card rounded-2xl px-5 py-6 text-center text-[16px] text-[color:var(--league-muted)]">
          {emptyMessage}
        </div>
      ) : (
        <>
          {/* Clipping window */}
          <div className="relative overflow-hidden">

            {/* Current pair — sets container height */}
            <div
              style={{
                transform: sliding ? 'translateY(-100%)' : 'translateY(0)',
                transition: reduceMotion ? 'none' : sliding ? TRANSITION : 'none',
              }}
            >
              <CardPair a={a} b={b} />
            </div>

            {/* Next pair — enters from below */}
            {total > 2 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  transform: sliding ? 'translateY(0)' : 'translateY(100%)',
                  transition: reduceMotion ? 'none' : sliding ? TRANSITION : 'none',
                }}
              >
                <CardPair a={c} b={d} />
              </div>
            )}
          </div>

          {/* Page dots. `flex-wrap`: con molte pagine (a 320 px «Ultimi
              risultati» ne ha 6) la riga dei pallini supera la larghezza dello
              schermo e, centrata, viene tagliata ai due bordi. Andando a capo
              restano tutti raggiungibili. */}
          {pageCount > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Pagina ${i + 1}`}
                  className="league-press flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-[color:var(--league-surface-alt)]"
                >
                  <span
                    aria-hidden
                    className={`rounded-full ${i === activePage
                      ? 'h-2 w-6 bg-[color:var(--league-brand-vivid)]'
                      : 'h-2 w-2 bg-[color:var(--league-line-strong)]'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
